import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { User, Download, Trash2, ShieldCheck } from "lucide-react";
import { SubscriptionCard } from "@/components/SubscriptionCard";
import { LiaBriefingCard } from "@/components/settings/LiaBriefingCard";
import { MerchantPinCard } from "@/components/settings/MerchantPinCard";

export default function Settings() {
  const { user, setUser } = useAuth();
  const navigate = useNavigate();


  const exportData = async () => {
    const { data } = await api.get("/account/export");
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = "respondia-mes-donnees.json"; a.click();
    URL.revokeObjectURL(url);
    toast.success("Données exportées");
  };

  const deleteAccount = async () => {
    try {
      await api.delete("/account");
      setUser(false);
      toast.success("Compte supprimé");
      navigate("/");
    } catch (e) { toast.error("Erreur lors de la suppression"); }
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-3xl mx-auto">
      <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight">Paramètres</h1>
      <p className="text-slate-500 mt-1">Gérez votre compte et vos données.</p>

      <div className="mt-6 space-y-5">
        <div className="bg-white rounded-2xl p-6 border border-slate-200" data-testid="account-info">
          <div className="flex items-center gap-2 mb-4"><User className="w-5 h-5 text-blue-600" /><h2 className="font-heading font-semibold">Mon compte</h2></div>
          <div className="grid sm:grid-cols-2 gap-4 text-sm">
            <div><span className="text-slate-400">Nom</span><p className="font-medium text-slate-800">{user?.name}</p></div>
            <div><span className="text-slate-400">Email</span><p className="font-medium text-slate-800">{user?.email}</p></div>
            <div><span className="text-slate-400">Rôle</span><p className="font-medium text-slate-800">{user?.role === "admin" ? "Administrateur" : "Utilisateur"}</p></div>
          </div>
        </div>

        <SubscriptionCard />

        <LiaBriefingCard />

        <MerchantPinCard />

        <div className="bg-white rounded-2xl p-6 border border-slate-200" data-testid="rgpd-section">
          <div className="flex items-center gap-2 mb-4"><ShieldCheck className="w-5 h-5 text-blue-600" /><h2 className="font-heading font-semibold">Mes données (RGPD)</h2></div>
          <Button variant="outline" onClick={exportData} data-testid="export-data-button" className="rounded-xl">
            <Download className="w-4 h-4 mr-2" /> Exporter mes données
          </Button>
        </div>

        {user?.role !== "admin" && (
          <div className="bg-red-50 rounded-2xl p-6 border border-red-200" data-testid="danger-zone">
            <h2 className="font-heading font-semibold text-red-700">Zone dangereuse</h2>
            <p className="text-sm text-red-600/80 mt-1 mb-4">La suppression de votre compte efface définitivement toutes vos données.</p>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="outline" data-testid="delete-account-button" className="rounded-xl border-red-300 text-red-600 hover:bg-red-100">
                  <Trash2 className="w-4 h-4 mr-2" /> Supprimer mon compte
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Supprimer définitivement votre compte ?</AlertDialogTitle>
                  <AlertDialogDescription>Toutes vos entreprises, établissements, audits et tâches seront supprimés. Cette action est irréversible.</AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Annuler</AlertDialogCancel>
                  <AlertDialogAction onClick={deleteAccount} className="bg-red-600 hover:bg-red-700" data-testid="confirm-delete-account">Supprimer</AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        )}
      </div>
    </div>
  );
}
